// Per-slug SEO overrides — high-traffic, low-competition long-tail keywords
// Targets Calculator.net's ranking keywords + 10 additional high-CPM low-comp terms.
// Applied dynamically by CalculatorPage.jsx.

export const HIGH_CPM_KEYWORDS = [
  'mortgage refinance calculator',       // high CPM
  'car loan payoff calculator',          // high CPM
  'debt consolidation loan calculator',  // very high CPM
  'personal injury settlement calculator', // high CPM
  'roth ira conversion calculator',      // high CPM
  'life insurance calculator',           // high CPM
  'business loan calculator',            // high CPM
  'health insurance premium calculator', // high CPM
  'annuity payout calculator',           // high CPM
  'HELOC payment calculator',            // high CPM
];

// Curated per-calculator meta data: title, description, extra keywords, FAQ (for JSON-LD).
export const SEO_MAP = {
  'mortgage': {
    title: 'Mortgage Calculator — Estimate Monthly Payment, PMI, Taxes (2026)',
    desc: 'Free mortgage calculator with taxes, insurance, PMI and HOA. Estimate monthly payment, total interest, and amortization schedule. Compare 15 vs 30 year loans.',
    keywords: 'mortgage calculator, mortgage payment calculator, home loan calculator, mortgage amortization, mortgage with taxes and insurance, PITI calculator, 30 year fixed, mortgage refinance calculator',
    faqs: [
      { q: 'How is a monthly mortgage payment calculated?', a: 'The monthly payment is calculated from loan amount, interest rate, and loan term using the standard amortization formula. Adding property taxes, homeowners insurance, PMI and HOA gives the full PITI payment.' },
      { q: 'What is PITI?', a: 'PITI stands for Principal, Interest, Taxes and Insurance — the four components of a typical monthly mortgage payment.' },
      { q: 'Do I need PMI?', a: 'Private Mortgage Insurance is usually required when your down payment is less than 20%. It can be removed once your equity reaches 20%.' },
    ],
  },
  'loan': {
    title: 'Loan Calculator — Calculate Monthly Payment & Total Interest',
    desc: 'Free loan calculator. Enter loan amount, rate, and term to see monthly payment, total interest, and full amortization schedule.',
    keywords: 'loan calculator, loan payment calculator, personal loan calculator, loan amortization, interest calculator, EMI calculator',
    faqs: [
      { q: 'How do I calculate a loan payment?', a: 'Use the formula M = P × r × (1+r)^n / ((1+r)^n − 1), where P is loan amount, r is monthly rate, and n is total months.' },
      { q: 'What is amortization?', a: 'Amortization is the process of paying off a loan through scheduled payments where each payment covers interest and principal.' },
    ],
  },
  'auto-loan': {
    title: 'Auto Loan Calculator — Car Payment, Interest & Total Cost (2026)',
    desc: 'Free auto loan calculator with trade-in, sales tax, and down payment. See monthly car payment, total interest, and out-the-door cost.',
    keywords: 'auto loan calculator, car loan calculator, car payment calculator, car loan payoff calculator, auto finance calculator, vehicle loan calculator',
  },
  'bmi': {
    title: 'BMI Calculator — Body Mass Index (Metric & US Units)',
    desc: 'Free BMI calculator. Enter weight and height in metric or US units to see your Body Mass Index category (underweight, normal, overweight, obese).',
    keywords: 'BMI calculator, body mass index calculator, healthy weight calculator, BMI chart, BMI formula',
    faqs: [
      { q: 'How is BMI calculated?', a: 'BMI = weight(kg) / height(m)². For US units: BMI = 703 × weight(lb) / height(in)².' },
      { q: 'What is a healthy BMI?', a: 'For most adults a BMI of 18.5–24.9 is considered normal weight.' },
    ],
  },
  'compound-interest': {
    title: 'Compound Interest Calculator — Grow Your Investment',
    desc: 'Free compound interest calculator. See how your money grows with monthly, quarterly, or annual compounding.',
    keywords: 'compound interest calculator, investment growth calculator, compounding calculator, savings interest calculator',
  },
  'retirement': {
    title: 'Retirement Calculator — 401k, IRA & Retirement Planning',
    desc: 'Free retirement calculator. Estimate how much you need to retire based on age, income, savings and expected return.',
    keywords: 'retirement calculator, 401k calculator, IRA calculator, retirement planning calculator, retirement savings calculator, roth ira conversion calculator',
  },
  'credit-card': {
    title: 'Credit Card Payoff Calculator — Snowball vs Avalanche',
    desc: 'Calculate how long to pay off credit card debt using snowball or avalanche methods. See total interest saved.',
    keywords: 'credit card payoff calculator, credit card interest calculator, debt payoff calculator, snowball method, avalanche method',
  },
  'percentage': {
    title: 'Percentage Calculator — Quick Percent of, Change & Increase',
    desc: 'Free percentage calculator. Calculate percent of, percent change, percent increase and decrease with step-by-step working.',
    keywords: 'percentage calculator, percent calculator, percent off calculator, percent change calculator, percent increase calculator',
  },
  'fraction': {
    title: 'Fraction Calculator — Add, Subtract, Multiply & Simplify',
    desc: 'Free fraction calculator. Add, subtract, multiply, divide, and simplify fractions with detailed step-by-step working.',
    keywords: 'fraction calculator, add fractions, subtract fractions, simplify fractions, mixed number calculator',
  },
  'scientific': {
    title: 'Scientific Calculator — Free Online Advanced Calculator',
    desc: 'Free online scientific calculator with sin, cos, tan, log, ln, exponents, factorial, memory and last-answer recall.',
    keywords: 'scientific calculator, online scientific calculator, trigonometry calculator, log calculator, exponent calculator',
  },
  'tax': {
    title: 'Income Tax Calculator 2026 — Estimate Federal Tax Refund',
    desc: 'Free 2026 income tax calculator. Estimate federal tax owed or refund based on filing status, income and deductions.',
    keywords: 'tax calculator, income tax calculator, federal tax calculator, tax refund calculator, 2026 tax calculator',
  },
  'calorie': {
    title: 'Calorie Calculator — TDEE, BMR & Daily Calorie Needs',
    desc: 'Free calorie calculator with BMR and TDEE. Find your daily calorie needs for weight loss, maintenance, or gain.',
    keywords: 'calorie calculator, TDEE calculator, BMR calculator, daily calorie needs, weight loss calculator',
  },
  'body-fat': {
    title: 'Body Fat Calculator — US Navy Method & Other Formulas',
    desc: 'Free body fat percentage calculator using the US Navy method. Estimate body fat from height and circumference measurements.',
    keywords: 'body fat calculator, body fat percentage calculator, US Navy body fat, body fat formula',
  },
  'debt-to-income': {
    title: 'Debt-to-Income Ratio Calculator — DTI for Loans',
    desc: 'Free DTI calculator. Compute your debt-to-income ratio for mortgage or personal loan qualification.',
    keywords: 'debt to income calculator, DTI calculator, debt ratio calculator, mortgage qualifier',
  },
  'rent-vs-buy': {
    title: 'Rent vs Buy Calculator — When Is Buying Cheaper?',
    desc: 'Free rent vs buy calculator with break-even year. Compare long-term cost of renting vs buying a home based on your market.',
    keywords: 'rent vs buy calculator, buy vs rent calculator, break-even calculator, home ownership calculator',
  },
  'age': {
    title: 'Age Calculator — Exact Age in Years, Months, Days',
    desc: 'Free age calculator. Find your exact age in years, months, days, hours or minutes from your birth date.',
    keywords: 'age calculator, exact age calculator, birthday calculator, age difference calculator',
  },
  'gpa': {
    title: 'GPA Calculator — Weighted & Unweighted GPA',
    desc: 'Free GPA calculator for high school and college. Calculate weighted, unweighted, and cumulative GPA.',
    keywords: 'GPA calculator, weighted GPA calculator, college GPA calculator, high school GPA calculator',
  },
  'ideal-weight': {
    title: 'Ideal Weight Calculator — Devine, Robinson & Miller Formulas',
    desc: 'Free ideal weight calculator using Devine, Robinson, Miller and Hamwi formulas. Includes healthy BMI range.',
    keywords: 'ideal weight calculator, ideal body weight, healthy weight, weight for height',
  },
  'time': {
    title: 'Time Calculator — Add, Subtract Hours, Minutes & Seconds',
    desc: 'Free time calculator. Add or subtract hours, minutes and seconds. Convert between time units and calculate elapsed time.',
    keywords: 'time calculator, time addition calculator, hours calculator, elapsed time calculator',
  },
  'investment': {
    title: 'Investment Calculator — Compound Growth, ROI & CAGR',
    desc: 'Free investment calculator. Project growth of your investment with regular contributions, compound interest, and inflation adjustment.',
    keywords: 'investment calculator, ROI calculator, CAGR calculator, investment return calculator, wealth calculator',
  },
};

export function getSeoForSlug(slug) {
  return SEO_MAP[slug] || null;
}
